import { Outlet } from "react-router-dom";
import Footer from "@/components/marketing/footer";
import Navbar from "@/components/marketing/navbar";
import ErrorBoundary from "@/components/ui/error-boundary";
import { Skeleton } from "@/components/ui/skeleton";
import React, { Suspense, useEffect, useState } from "react";
import { useWindowLoad } from "@/hooks/useWindowLoad";

const MarketingLayout = () => {
  const [scrollY, setScrollY] = useState(0);
  const isWindowLoaded = useWindowLoad();

  useEffect(() => {
    let rafId: number | null = null;
    const lastScrollY = { current: window.scrollY };

    const handleScroll = () => {
      if (rafId !== null) return;

      rafId = window.requestAnimationFrame(() => {
        const nextScrollY = window.scrollY;

        if (Math.abs(nextScrollY - lastScrollY.current) >= 1) {
          lastScrollY.current = nextScrollY;
          setScrollY(nextScrollY);
        }

        rafId = null;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Calculate parallax and fade effects
  const parallaxOffset = scrollY * 0.5;
  const fadeOpacity = Math.max(0, 1 - scrollY / 600);

  if (!isWindowLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-oid="preload:layout">
        <div className="space-y-4 w-full max-w-md px-6">
          <Skeleton className="h-12 w-3/4 mx-auto" data-oid="preload:heading" />
          <div className="grid grid-cols-3 gap-4">
            <Skeleton className="h-16" data-oid="preload:tile1" />
            <Skeleton className="h-16" data-oid="preload:tile2" />
            <Skeleton className="h-16" data-oid="preload:tile3" />
          </div>
          <Skeleton className="h-48" data-oid="preload:body" />
        </div>
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-background" data-oid=".h8ko62" />
      }
      data-oid="lx:2yhp"
    >
      <div className="min-h-screen relative bg-background overflow-x-hidden" data-oid="eriw:3z">
        {/* Building Banner Background - YouTube Style with Parallax */}
        <div
          className="fixed top-0 left-0 w-full h-[100vh] z-10 transition-opacity duration-75"
          style={{
            opacity: fadeOpacity,
            transform: `translateY(${parallaxOffset}px)`,
          }}
          data-oid="sxx7f2a"
        >
          <div
            className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: "url(/building.png)",
              backgroundPosition: "center top",
            }}
            data-oid="8ucyik:"
          />

          {/* Seamless blend overlay for dark theme integration */}
          <div
            className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 via-black/60 to-background/95"
            data-oid="5-snkki"
          />

          {/* Extended bottom fade for seamless transition */}
          <div
            className="absolute bottom-0 left-0 w-full h-[40vh] bg-gradient-to-t from-background via-background/80 to-transparent"
            data-oid="cxoel48"
          />
        </div>

        {/* Content with proper layering */}
        <div className="relative z-20" data-oid="u6ra9ba">
          <Navbar data-oid="lt59i::" />
          <main className="flex-1 relative z-30" data-oid="3c..azr">
            <ErrorBoundary data-oid="eno._j.">
              <Suspense
                fallback={
                  <div
                    className="min-h-screen flex items-center justify-center"
                    data-oid="5yvywq-"
                  >
                    <div
                      className="space-y-4 w-full max-w-4xl mx-auto p-6"
                      data-oid="l1cp75e"
                    >
                      <Skeleton
                        className="h-12 w-3/4 mx-auto"
                        data-oid="jvi2pjt"
                      />

                      <div
                        className="grid grid-cols-1 md:grid-cols-3 gap-4"
                        data-oid="l1qsttk"
                      >
                        <Skeleton className="h-32 w-full" data-oid="rxt-j2l" />
                        <Skeleton className="h-32 w-full" data-oid="d45xf1_" />
                        <Skeleton className="h-32 w-full" data-oid="j2s4s4z" />
                      </div>
                      <Skeleton className="h-64 w-full" data-oid="t5m..tq" />
                    </div>
                  </div>
                }
                data-oid="mo:ue.b"
              >
                <Outlet data-oid="mqg9fxp" />
              </Suspense>
            </ErrorBoundary>
          </main>
          <Footer data-oid="kpm_aje" />
        </div>
      </div>
    </Suspense>
  );
};

export default MarketingLayout;
